import type { CSSProperties } from 'react'

/**
 * Период списка обращений: пресеты (часы назад) или свой диапазон дат создания.
 * `created_period` в URL нужен, чтобы «Своё» не сливалось с пресетом 14 дней (336 ч).
 */

export const TICKETS_LIST_PERIOD_PRESET_HOURS: Record<string, number> = {
  '1': 24,
  '7': 168,
  '14': 336,
  '30': 720,
}

export type TicketsListPeriodKey = '1' | '7' | '14' | '30' | 'all' | 'custom'

const PRESET_KEYS = new Set(Object.keys(TICKETS_LIST_PERIOD_PRESET_HOURS))

export function isTicketsListPeriodKey(v: string): v is TicketsListPeriodKey {
  return v === 'all' || v === 'custom' || PRESET_KEYS.has(v)
}

function toYmdLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function defaultTicketsListCustomCreatedDates(): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 13)
  return { from: toYmdLocal(from), to: toYmdLocal(to) }
}

/** Диапазон дат из URL или из «произвольных» часов (старые ссылки без created_period). */
export function ticketsListCustomCreatedDatesFromParams(sp: URLSearchParams): {
  from: string
  to: string
} {
  const fromUrl = sp.get('created_date_from')?.trim()
  const toUrl = sp.get('created_date_to')?.trim()
  if (fromUrl && toUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl) && /^\d{4}-\d{2}-\d{2}$/.test(toUrl)) {
    return { from: fromUrl, to: toUrl }
  }
  const cwh = sp.get('created_within_hours')
  const h = parseInt(cwh || '', 10)
  if (!Number.isNaN(h) && h >= 24) {
    const to = new Date()
    const from = new Date()
    const days = Math.max(1, Math.min(365, Math.round(h / 24)))
    from.setDate(from.getDate() - (days - 1))
    return { from: toYmdLocal(from), to: toYmdLocal(to) }
  }
  return defaultTicketsListCustomCreatedDates()
}

export function ticketsListPeriodSelectValue(sp: URLSearchParams): TicketsListPeriodKey {
  const explicit = sp.get('created_period')?.trim()
  if (explicit === 'custom') return 'custom'
  if (explicit === 'all') return 'all'
  if (explicit && PRESET_KEYS.has(explicit)) return explicit as TicketsListPeriodKey

  if (sp.get('created_date_from')?.trim() && sp.get('created_date_to')?.trim()) {
    return 'custom'
  }

  const cwh = sp.get('created_within_hours')
  if (!cwh) return 'all'
  const h = parseInt(cwh, 10)
  if (Number.isNaN(h)) return 'all'
  const entry = Object.entries(TICKETS_LIST_PERIOD_PRESET_HOURS).find(([, v]) => v === h)
  if (entry) return entry[0] as TicketsListPeriodKey
  return 'custom'
}

export const ticketsListPeriodDateInputStyle: CSSProperties = {
  padding: '4px 8px',
  borderRadius: 6,
  border: '1px solid var(--mantine-color-default-border)',
  background: 'var(--mantine-color-body)',
  color: 'var(--mantine-color-text)',
  fontSize: 12,
  minHeight: 28,
}
